import { useEffect, useMemo, useRef, useState } from "react";
import { Animated, Easing } from "react-native";
import { SPOTLIGHT_TRANSITION_DURATION } from "./constants";

export default function useSpotlightAnimation(
  visible: boolean,
  offscreenY: number
) {
  const translateY = useRef(
    new Animated.Value(visible ? 0 : offscreenY)
  ).current;
  const opacity = useRef(new Animated.Value(visible ? 1 : 0)).current;
  const animationRef = useRef<Animated.CompositeAnimation | null>(null);
  const [mounted, setMounted] = useState(visible);

  useEffect(() => {
    animationRef.current?.stop();

    if (visible) {
      setMounted(true);
      animationRef.current = Animated.parallel([
        Animated.timing(translateY, {
          toValue: 0,
          duration: SPOTLIGHT_TRANSITION_DURATION,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 1,
          duration: SPOTLIGHT_TRANSITION_DURATION,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]);
      animationRef.current.start(() => {
        animationRef.current = null;
      });
      return () => animationRef.current?.stop();
    }

    animationRef.current = Animated.parallel([
      Animated.timing(translateY, {
        toValue: offscreenY,
        duration: SPOTLIGHT_TRANSITION_DURATION,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        toValue: 0,
        duration: SPOTLIGHT_TRANSITION_DURATION,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
    ]);

    animationRef.current.start(() => {
      animationRef.current = null;
      setMounted(false);
    });

    return () => animationRef.current?.stop();
  }, [visible, translateY, opacity, offscreenY]);

  const animatedStyle = useMemo(
    () => ({
      transform: [{ translateY }],
      opacity,
    }),
    [translateY, opacity]
  );

  return { mounted, animatedStyle };
}
